import * as React from 'react'
import {makeStyles} from '@oc/styles'
import {Accordion, AccordionSummary, AccordionDetails} from '@mui/material'
import {messages} from './texts'
import {useLocation} from 'react-router'

const useErrorViewStyles = makeStyles(theme => ({
    root: {
        width: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
    },
    message: {
        padding: theme.spacing(2)
    },
    accordion: {
        boxShadow: 'none'
    },
    summary: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText
    },
    summaryContent: {
        textAlign: 'center',
        flex: '1 1 100%'
    },
    details: {
        background: theme.palette.error.main,
        color: theme.palette.error.contrastText,
        flexWrap: 'wrap',
        maxHeight: '80vh',
        overflow: 'auto'
    },
    messageHead: {
        flex: '1 1 100%'
    },
    stack: {
        flex: '1 1 100%'
    }
}))

interface ErrorViewProps {
    error: Error | null | undefined
    errorInfo: React.ErrorInfo | null | undefined
}

function ErrorView({error, errorInfo}: ErrorViewProps) {
    const {classes} = useErrorViewStyles()

    return (
        <div className={classes.root}>
            <div className={classes.message}>
                <Accordion className={classes.accordion}>
                    <AccordionSummary className={classes.summary}>
                        <div className={classes.summaryContent}>{messages.getText('errorBoundary', 'header')}</div>
                    </AccordionSummary>
                    <AccordionDetails className={classes.details}>
                        {error ? (
                            <pre className={classes.stack}>{error.stack || ''}</pre>
                        ) : (
                            <div className={classes.messageHead}>{messages.getText('errorBoundary', 'noError')}</div>
                        )}

                        {errorInfo ? (
                            <>
                                <hr className={classes.stack} />
                                <pre className={classes.stack}>{errorInfo.componentStack || ''}</pre>
                            </>
                        ) : null}
                    </AccordionDetails>
                </Accordion>
            </div>
        </div>
    )
}

interface State {
    error?: Error | null
    errorInfo?: React.ErrorInfo | null
}

class ViewErrorBoundaryInternal extends React.PureComponent<React.PropsWithChildren, State> {
    public override state = {
        error: null,
        errorInfo: null
    }

    public onHistoryChanged = () => {
        this.setState({
            error: null,
            errorInfo: null
        })
    }

    public override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        this.setState({error, errorInfo})
    }

    public override render(): React.ReactNode {
        const {
            props: {children},
            state: {error, errorInfo}
        } = this

        return error || errorInfo ? <ErrorView error={error} errorInfo={errorInfo} /> : children
    }
}

export function ViewErrorBoundary(props: React.PropsWithChildren) {
    const viewErrorRef = React.useRef<ViewErrorBoundaryInternal | null>(null)
    const location = useLocation()

    React.useEffect(() => {
        viewErrorRef.current?.onHistoryChanged()
    }, [location.pathname, location.search])

    return <ViewErrorBoundaryInternal ref={viewErrorRef}>{props.children}</ViewErrorBoundaryInternal>
}
